import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { mergeStyles } from '@fluentui/react';
import { Button, Divider, SpinButton, Switch, makeStyles } from '@fluentui/react-components';
import { AddRegular, DeleteRegular, TrophyRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import {
  cardContentWrapperStyles,
  cardWrapperStyles,
  iconButtonStyles,
  MessagingColors,
} from '../../../../app/common/styles/CommonStyleObjects';
import { IProcessTemplateMilestone } from '../../../../utils/types/IProcessTemplate';
import SpanWithLabel from '../../../common/SpanWithLabel';
import DeleteConfirmationDialog from '../../../common/DeleteConfirmationDialog';
import { IPanelState } from './ProcessTemplateMilestoneEdit';

const useEditItemStyles = makeStyles({
  nameColumn: { flex: '0 0 20%' },
  stackColumn: { flexBasis: '0', minWidth: '100px' },
  buttonSeparator: { margin: '25px 15px' },
  actionsColumn: { flexGrow: 4, flexBasis: '0', minWidth: '100px' },
});

interface ProcessTemplateMilestoneEditItemProps {
  milestone: IProcessTemplateMilestone;
  setIsAddMilestonePanelOpen: (item: IPanelState) => void;
  onDurationChange: (duration: number, id: number) => string;
  onDeleteMilestone: (id: number) => void;
  index: number;
  setGoalMilestone: (index: number) => void;
  onEnforceMinDurationChange: (checked: boolean, id: number) => void;
}
const ProcessTemplateMilestoneEditItem = (
  props: ProcessTemplateMilestoneEditItemProps,
): JSX.Element => {
  const {
    milestone,
    setIsAddMilestonePanelOpen,
    onDurationChange,
    onDeleteMilestone,
    index,
    setGoalMilestone,
    onEnforceMinDurationChange,
  } = props;
  const editClasses = useEditItemStyles();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const hideDeleteDialog = (): void => {
    setIsDeleteConfirmOpen(false);
  };

  const deleteButtonClick = (): void => {
    setIsDeleteConfirmOpen(true);
  };

  const deleteConfirmClick = (): void => {
    hideDeleteDialog();
    onDeleteMilestone(milestone.id);
  };

  const getSignoffMilestoneStyles = (): string => {
    if (milestone.defaultDaysOffset === 0) {
      return mergeStyles({ background: MessagingColors.warning });
    }
    return '';
  };

  const buttonSeparatorClassName = editClasses.buttonSeparator;
  const durationWidth = { width: 80 };

  const getDurationInput = (): JSX.Element => {
    if (index !== 1)
      return (
        <SpinButton
          style={durationWidth}
          aria-label="Duration"
          value={milestone.duration as number}
          step={1}
          onChange={(_e, data) => {
            let newValue: number | undefined;
            if (data.value !== undefined && data.value !== null) {
              newValue = data.value;
            } else if (data.displayValue) {
              const parsed = parseFloat(data.displayValue);
              if (Number.isFinite(parsed)) {
                newValue = Math.round(parsed);
              }
            }
            if (newValue !== undefined) {
              onDurationChange(newValue, milestone.id);
            }
          }}
        />
      );
    return <div style={durationWidth}>--</div>;
  };

  const stackColumnClassName = editClasses.stackColumn;

  const getEnforceMinDurationInput = (): JSX.Element => {
    if (index !== 1)
      return (
        <Switch
          style={{ marginBottom: '0' }}
          label={milestone.enforceDefaultAsMinDuration ? 'On' : 'Off '}
          aria-label="Enforce duration as minimum"
          checked={milestone.enforceDefaultAsMinDuration}
          onChange={(e: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }): void => {
            onEnforceMinDurationChange(data.checked, milestone.id);
          }}
        />
      );
    return <div>--</div>;
  };

  return (
    <>
      <div className={`ms-depth-4 ${cardWrapperStyles} ${getSignoffMilestoneStyles()}`}>
        <div className={cardContentWrapperStyles}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 30 }} wrap>
            <StackItem className={editClasses.nameColumn}>
              <SpanWithLabel labelText="Milestone Name" value={milestone.name} />
            </StackItem>
            {milestone.milestoneCategoryName && (
              <StackItem grow={2} className={stackColumnClassName}>
                <SpanWithLabel labelText="Category" value={milestone.milestoneCategoryName} />
              </StackItem>
            )}
            <StackItem grow={1} className={stackColumnClassName}>
              <SpanWithLabel labelText="Duration">{getDurationInput()}</SpanWithLabel>
            </StackItem>
            <StackItem grow={2} className={stackColumnClassName}>
              <SpanWithLabel labelText="Enforce duration as minimum">
                {getEnforceMinDurationInput()}
              </SpanWithLabel>
            </StackItem>
            <StackItem grow={2} className={stackColumnClassName}>
              <SpanWithLabel
                labelText="Days from Filing Date"
                value={Math.abs(milestone.defaultDaysOffset)}
              />
            </StackItem>
            <StackItem className={editClasses.actionsColumn}>
              <Stack horizontal verticalAlign="center">
                <Button
                  appearance="transparent"
                  icon={<DeleteRegular color={iconButtonStyles.color} />}
                  title="Delete Milestone"
                  aria-label="Delete Milestone"
                  disabled={milestone.defaultDaysOffset === 0}
                  onClick={deleteButtonClick}
                >
                  Delete Milestone
                </Button>
                <div>|</div>
                <Button
                  appearance="transparent"
                  icon={<TrophyRegular color={iconButtonStyles.color} />}
                  title="Set As Goal Milestone"
                  aria-label="Set As Goal Milestone"
                  disabled={milestone.defaultDaysOffset === 0}
                  onClick={() => {
                    setGoalMilestone(index);
                  }}
                >
                  Set As Goal Milestone
                </Button>
              </Stack>
            </StackItem>
          </Stack>
        </div>
      </div>

      <Stack
        className={buttonSeparatorClassName}
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 20 }}
      >
        <Button
          icon={<AddRegular />}
          title="Add Milestone to Template"
          aria-label="Add Milestone to Template"
          onClick={() => setIsAddMilestonePanelOpen({ panelIsOpen: true, index })}
        >
          Add Milestone
        </Button>
        <StackItem grow>
          <Divider />
        </StackItem>
      </Stack>
      <DeleteConfirmationDialog
        open={isDeleteConfirmOpen}
        message="Are you sure you want to delete this milestone?"
        onConfirm={deleteConfirmClick}
        onCancel={hideDeleteDialog}
      />
    </>
  );
};
export default ProcessTemplateMilestoneEditItem;
